/**
 * Formulaire de profil (onboarding + édition). Calcule en direct âge, BMR
 * (Mifflin-St Jeor), TDEE et seuil calorique suggéré ; bascule auto/manuel.
 */
import { type FormEvent, useMemo, useState } from 'react';
import { Kpi, RangeWithGuideline, Segmented } from '@/components/ui';
import {
  ACTIVITY_DESCRIPTIONS,
  ACTIVITY_LABELS,
  type ActivityLevel,
  ageFrom,
  bmrMifflinStJeor,
  deriveMacroTargets,
  FAT_PCT_RECO,
  macroTargetsFromGrams,
  PROTEIN_PER_KG,
  PROTEIN_PER_KG_BY_PHASE,
  resolveCalorieTarget,
  SAFE_FLOOR_KCAL,
  SAT_FAT_MAX_PCT,
  type Sex,
  SURPLUS_KCAL_RECO,
  suggestProteinTarget,
  suggestWaterTarget,
  TRAINING_PHASE_LABELS,
  TRAINING_PHASE_ORDER,
  type TrainingPhase,
  tdee,
  WATER_ML_PER_KG,
  WATER_ML_PER_KG_RECO,
} from '@/domain';

/** Bascule auto/manuel des objectifs (seuil, protéines, macros, eau). */
const MODE_OPTIONS = [
  { key: 'auto' as const, label: 'Suggéré' },
  { key: 'manual' as const, label: 'Manuel' },
];

const PHASE_OPTIONS = TRAINING_PHASE_ORDER.map((key) => ({
  key,
  label: TRAINING_PHASE_LABELS[key],
}));

export interface ProfileFormValues {
  birthdate: string;
  height_cm: number;
  sex: Sex;
  activity_level: ActivityLevel;
  ideal_weight_kg: number;
  target_weight_kg: number;
  calorie_threshold: number;
  threshold_mode: 'auto' | 'manual';
  protein_target_g: number;
  protein_target_mode: 'auto' | 'manual';
  protein_per_kg: number;
  email_reminders: boolean;
  push_reminders: boolean;
  macro_target_mode: 'auto' | 'manual';
  fat_pct: number;
  carb_target_g: number;
  fat_target_g: number;
  water_target_ml: number;
  water_target_mode: 'auto' | 'manual';
  water_ml_per_kg: number;
  /** Rythme de perte visé (kg/sem) qui pilote le seuil en sèche — persisté. */
  weekly_loss_kg: number;
  /** Phase d'entraînement : oriente la bande protéique ET la direction du seuil. */
  training_phase: TrainingPhase;
  /** Surplus (kcal/j) au-dessus de la maintenance, pilote le seuil en prise de masse. */
  surplus_kcal: number;
}

interface Props {
  initial?: Partial<ProfileFormValues & { currentWeightKg: number }>;
  /** Affiche le champ « poids actuel » (onboarding : crée la 1re pesée). */
  askCurrentWeight?: boolean;
  submitLabel: string;
  onSubmit: (values: ProfileFormValues, currentWeightKg?: number) => Promise<void>;
}

const ACTIVITIES: ActivityLevel[] = ['sedentary', 'light', 'moderate', 'active', 'very_active'];

export function ProfileForm({ initial, askCurrentWeight, submitLabel, onSubmit }: Props) {
  const [birthdate, setBirthdate] = useState(initial?.birthdate ?? '1990-01-01');
  const [heightCm, setHeightCm] = useState(initial?.height_cm ?? 175);
  const [sex, setSex] = useState<Sex>(initial?.sex ?? 'male');
  const [activity, setActivity] = useState<ActivityLevel>(initial?.activity_level ?? 'moderate');
  const [currentWeight, setCurrentWeight] = useState(initial?.currentWeightKg ?? 80);
  const [idealWeight, setIdealWeight] = useState(initial?.ideal_weight_kg ?? 72);
  const [targetWeight, setTargetWeight] = useState(initial?.target_weight_kg ?? 75);
  // Rythme de perte visé, depuis la valeur persistée (défaut 0.5).
  const [weeklyLoss, setWeeklyLoss] = useState(initial?.weekly_loss_kg || 0.5);
  const [mode, setMode] = useState<'auto' | 'manual'>(initial?.threshold_mode ?? 'auto');
  const [manualThreshold, setManualThreshold] = useState(initial?.calorie_threshold ?? 2000);
  const [proteinMode, setProteinMode] = useState<'auto' | 'manual'>(
    initial?.protein_target_mode ?? 'auto',
  );
  const [trainingPhase, setTrainingPhase] = useState<TrainingPhase>(
    initial?.training_phase ?? 'cut',
  );
  // Surplus visé en prise de masse (kcal/j), défaut milieu de bande sourcée.
  const [surplusKcal, setSurplusKcal] = useState(
    initial?.surplus_kcal || SURPLUS_KCAL_RECO.default,
  );
  const [proteinPerKg, setProteinPerKg] = useState(
    // Profil existant : valeur persistée. Nouveau profil : milieu de bande de la
    // phase initiale (évite un curseur sous la zone recommandée à l'onboarding).
    initial?.protein_per_kg || PROTEIN_PER_KG_BY_PHASE[initial?.training_phase ?? 'cut'].default,
  );
  const proteinBand = PROTEIN_PER_KG_BY_PHASE[trainingPhase];
  // Changement EXPLICITE de phase → recale la reco protéique au milieu de bande.
  // (Jamais au montage : les profils existants gardent leur valeur persistée.)
  function changePhase(p: TrainingPhase) {
    if (p === trainingPhase) return; // re-clic sur la phase active : ne pas écraser un réglage custom
    setTrainingPhase(p);
    setProteinPerKg(PROTEIN_PER_KG_BY_PHASE[p].default);
  }
  const [manualProtein, setManualProtein] = useState(initial?.protein_target_g ?? 130);
  const [emailReminders, setEmailReminders] = useState(initial?.email_reminders ?? false);
  const [macroMode, setMacroMode] = useState<'auto' | 'manual'>(
    initial?.macro_target_mode ?? 'auto',
  );
  const [fatPct, setFatPct] = useState(initial?.fat_pct ?? 30);
  const [manualCarb, setManualCarb] = useState(initial?.carb_target_g ?? 200);
  const [manualFat, setManualFat] = useState(initial?.fat_target_g ?? 70);
  const [waterMode, setWaterMode] = useState<'auto' | 'manual'>(
    initial?.water_target_mode ?? 'auto',
  );
  const [waterMlPerKg, setWaterMlPerKg] = useState(
    initial?.water_ml_per_kg || WATER_ML_PER_KG.default,
  );
  const [manualWater, setManualWater] = useState(initial?.water_target_ml ?? 2500);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  // Chaîne de calcul métier recalculée à chaque saisie. Le seuil auto dépend de la
  // phase : sèche = déficit, prise de masse = TDEE + surplus, récupération = maintenance.
  const derived = useMemo(() => {
    const age = ageFrom(birthdate);
    const bmr = bmrMifflinStJeor(currentWeight, heightCm, age, sex);
    const maintenance = tdee(bmr, activity);
    const target = resolveCalorieTarget(
      { trainingPhase, weeklyLossKg: weeklyLoss, surplusKcal },
      maintenance,
      sex,
    );
    return { age, bmr, maintenance, target };
  }, [birthdate, currentWeight, heightCm, sex, activity, trainingPhase, weeklyLoss, surplusKcal]);

  const threshold = mode === 'auto' ? derived.target.threshold : manualThreshold;
  const suggestedProtein = suggestProteinTarget(currentWeight, proteinPerKg);
  const proteinTarget = proteinMode === 'auto' ? suggestedProtein : manualProtein;

  // Macros : dérivées (auto) ou grammes fixés (manuel).
  const macroAuto = deriveMacroTargets(Math.round(threshold), Math.round(proteinTarget), fatPct);
  const carbTarget = macroMode === 'auto' ? macroAuto.carbG : manualCarb;
  const fatTarget = macroMode === 'auto' ? macroAuto.fatG : manualFat;
  const suggestedWater = suggestWaterTarget(currentWeight, waterMlPerKg);
  const waterTarget = waterMode === 'auto' ? suggestedWater : manualWater;

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError('');
    setBusy(true);
    try {
      await onSubmit(
        {
          birthdate,
          height_cm: heightCm,
          sex,
          activity_level: activity,
          ideal_weight_kg: idealWeight,
          target_weight_kg: targetWeight,
          calorie_threshold: Math.round(threshold),
          threshold_mode: mode,
          protein_target_g: Math.round(proteinTarget),
          protein_target_mode: proteinMode,
          protein_per_kg: proteinPerKg,
          email_reminders: emailReminders,
          // push_reminders piloté ailleurs (permission navigateur) : on préserve l'existant.
          push_reminders: initial?.push_reminders ?? false,
          macro_target_mode: macroMode,
          fat_pct: fatPct,
          carb_target_g: Math.round(carbTarget),
          fat_target_g: Math.round(fatTarget),
          water_target_mode: waterMode,
          water_ml_per_kg: waterMlPerKg,
          water_target_ml: Math.round(waterTarget),
          weekly_loss_kg: weeklyLoss,
          training_phase: trainingPhase,
          surplus_kcal: surplusKcal,
        },
        askCurrentWeight ? currentWeight : undefined,
      );
    } catch (err) {
      setError('Enregistrement impossible. Réessaie.');
      console.error(err);
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={submit}>
      <div className="row">
        <div className="field">
          <label htmlFor="birthdate">Date de naissance</label>
          <input
            id="birthdate"
            type="date"
            value={birthdate}
            onChange={(e) => setBirthdate(e.target.value)}
            required
          />
        </div>
        <div className="field">
          <label htmlFor="sex">Sexe</label>
          <select id="sex" value={sex} onChange={(e) => setSex(e.target.value as Sex)}>
            <option value="male">Homme</option>
            <option value="female">Femme</option>
          </select>
        </div>
        <div className="field">
          <label htmlFor="height">Taille (cm)</label>
          <input
            id="height"
            type="number"
            min={50}
            max={260}
            value={heightCm}
            onChange={(e) => setHeightCm(Number(e.target.value))}
            required
          />
        </div>
      </div>

      <div className="row">
        {askCurrentWeight && (
          <div className="field">
            <label htmlFor="cw">Poids actuel (kg)</label>
            <input
              id="cw"
              type="number"
              step="0.1"
              min={20}
              max={400}
              value={currentWeight}
              onChange={(e) => setCurrentWeight(Number(e.target.value))}
              required
            />
          </div>
        )}
        <div className="field">
          <label htmlFor="target">Poids cible (kg)</label>
          <input
            id="target"
            type="number"
            step="0.1"
            min={20}
            max={400}
            value={targetWeight}
            onChange={(e) => setTargetWeight(Number(e.target.value))}
            required
          />
        </div>
        <div className="field">
          <label htmlFor="ideal">Poids idéal (kg)</label>
          <input
            id="ideal"
            type="number"
            step="0.1"
            min={20}
            max={400}
            value={idealWeight}
            onChange={(e) => setIdealWeight(Number(e.target.value))}
            required
          />
        </div>
      </div>

      <div className="field">
        <label htmlFor="activity">Niveau d'activité</label>
        <select
          id="activity"
          value={activity}
          onChange={(e) => setActivity(e.target.value as ActivityLevel)}
        >
          {ACTIVITIES.map((a) => (
            <option key={a} value={a}>
              {ACTIVITY_LABELS[a]}
            </option>
          ))}
        </select>
        <span className="muted" style={{ fontSize: '0.82rem' }}>
          {ACTIVITY_DESCRIPTIONS[activity]}
        </span>
      </div>

      {/* Résumé des calculs métier en direct */}
      <div className="card" style={{ background: 'var(--surface-2)', margin: '0.5rem 0 1rem' }}>
        <div className="grid kpis" style={{ gap: '0.75rem' }}>
          <Kpi label="Âge" value={`${derived.age} ans`} />
          <Kpi label="Métabolisme de base" value={`${Math.round(derived.bmr)} kcal`} />
          <Kpi label="Maintenance (TDEE)" value={`${Math.round(derived.maintenance)} kcal`} />
          {derived.target.phase === 'cut' && (
            <Kpi label="Perte estimée" value={`${derived.target.suggestion.weeklyLossKg} kg/sem`} />
          )}
          {derived.target.phase === 'bulk' && (
            <Kpi label="Surplus" value={`+${derived.target.surplusKcal} kcal/j`} />
          )}
          {derived.target.phase === 'recovery' && <Kpi label="Cible" value="Maintenance" />}
        </div>
      </div>

      <div className="field">
        <span className="field-label">Phase d'entraînement</span>
        <Segmented
          label="Phase d'entraînement"
          value={trainingPhase}
          options={PHASE_OPTIONS}
          onChange={changePhase}
        />
        <span className="muted" style={{ fontSize: '0.78rem' }}>
          La phase ajuste les repères de <strong>calories</strong> ET de <strong>protéines</strong>.
        </span>
      </div>

      {/* Contrôle calorique selon la phase : sèche = déficit, prise de masse =
          surplus, récupération = maintenance (Tipton 2015). */}
      {trainingPhase === 'cut' && (
        <div className="field">
          <label htmlFor="weekly">Rythme de perte visé : {weeklyLoss.toFixed(2)} kg/semaine</label>
          <input
            id="weekly"
            type="range"
            min={0.1}
            max={1}
            step={0.05}
            value={weeklyLoss}
            onChange={(e) => setWeeklyLoss(Number(e.target.value))}
            disabled={mode === 'manual'}
          />
          {derived.target.phase === 'cut' &&
            derived.target.suggestion.flooredBySafety &&
            mode === 'auto' && (
              <span className="muted" style={{ fontSize: '0.78rem' }}>
                ⚠️ Seuil ramené au plancher de sécurité ({SAFE_FLOOR_KCAL[sex]} kcal).
              </span>
            )}
        </div>
      )}

      {trainingPhase === 'bulk' && mode === 'auto' && (
        <RangeWithGuideline
          label={`Surplus visé : +${surplusKcal} kcal/jour`}
          // Min 100 (et non 0) : un surplus nul en prise de masse n'a pas de sens
          // (la phase récupération existe pour viser la maintenance). Rend `|| 350`
          // sûr côté mapper (0 = valeur non renseignée, comme weekly_loss_kg).
          min={100}
          max={800}
          step={10}
          value={surplusKcal}
          onChange={setSurplusKcal}
          reco={{ min: SURPLUS_KCAL_RECO.min, max: SURPLUS_KCAL_RECO.max }}
          note={`Recommandé : +${SURPLUS_KCAL_RECO.min}–${SURPLUS_KCAL_RECO.max} kcal/j (${SURPLUS_KCAL_RECO.source}). La prise de masse demande surplus ET protéines suffisantes (le surplus se remplit surtout de glucides/lipides). Pas de projection de gain en kg : le rythme n'est pas validé scientifiquement.`}
        />
      )}

      {trainingPhase === 'recovery' && (
        <p className="muted" style={{ fontSize: '0.82rem', margin: '0 0 0.9rem' }}>
          En récupération, la cible = <strong>maintenance</strong> (ni déficit ni surplus) — viser
          l'équilibre énergétique favorise la récupération musculaire (Tipton 2015).
        </p>
      )}

      <div className="field">
        <span className="field-label">Seuil calorique quotidien</span>
        <div className="toolbar" style={{ marginBottom: '0.5rem' }}>
          <Segmented
            label="Mode du seuil calorique"
            value={mode}
            options={MODE_OPTIONS}
            onChange={setMode}
          />
          {mode === 'manual' ? (
            <input
              type="number"
              min={800}
              max={6000}
              value={manualThreshold}
              onChange={(e) => setManualThreshold(Number(e.target.value))}
              style={{ maxWidth: 160 }}
            />
          ) : (
            <strong style={{ fontSize: '1.3rem' }}>{Math.round(threshold)} kcal</strong>
          )}
        </div>
      </div>

      <div className="field">
        <span className="field-label">Objectif protéique quotidien</span>
        <div className="toolbar" style={{ marginBottom: '0.5rem' }}>
          <Segmented
            label="Mode de l'objectif protéique"
            value={proteinMode}
            options={MODE_OPTIONS}
            onChange={setProteinMode}
          />
          {proteinMode === 'manual' ? (
            <input
              type="number"
              min={0}
              max={400}
              value={manualProtein}
              onChange={(e) => setManualProtein(Number(e.target.value))}
              style={{ maxWidth: 160 }}
            />
          ) : (
            <strong style={{ fontSize: '1.3rem' }}>{Math.round(proteinTarget)} g</strong>
          )}
        </div>
        {proteinMode === 'auto' && (
          <RangeWithGuideline
            label={`Ratio : ${proteinPerKg.toFixed(2)} g/kg`}
            min={PROTEIN_PER_KG.min}
            max={PROTEIN_PER_KG.max}
            step={0.05}
            value={proteinPerKg}
            onChange={setProteinPerKg}
            reco={{ min: proteinBand.min, max: proteinBand.max }}
            note={`Recommandé (${TRAINING_PHASE_LABELS[trainingPhase]}) : ${proteinBand.min}–${proteinBand.max} g/kg (${proteinBand.source})`}
          />
        )}
      </div>

      <div className="field">
        <span className="field-label">Objectifs glucides &amp; lipides</span>
        <div className="toolbar" style={{ marginBottom: '0.5rem' }}>
          <Segmented
            label="Mode des objectifs macros"
            value={macroMode}
            options={MODE_OPTIONS}
            onChange={setMacroMode}
          />
        </div>
        {macroMode === 'auto' ? (
          <>
            <RangeWithGuideline
              label={`Part des lipides : ${fatPct}% des calories`}
              min={15}
              max={45}
              step={1}
              value={fatPct}
              onChange={setFatPct}
              reco={{ min: FAT_PCT_RECO.min, max: FAT_PCT_RECO.max }}
              note={`Recommandé : ${FAT_PCT_RECO.min}–${FAT_PCT_RECO.max}% des calories (${FAT_PCT_RECO.source}) · acides gras saturés ≤ ${SAT_FAT_MAX_PCT}% (OMS)`}
            />
            <span className="muted" style={{ fontSize: '0.82rem' }}>
              → {macroAuto.proteinG} g protéines · {macroAuto.carbG} g glucides · {macroAuto.fatG} g
              lipides
            </span>
          </>
        ) : (
          <>
            <div className="row">
              <div className="field" style={{ marginBottom: 0 }}>
                <label htmlFor="carbg">Glucides (g)</label>
                <input
                  id="carbg"
                  type="number"
                  min={0}
                  max={1000}
                  value={manualCarb}
                  onChange={(e) => setManualCarb(Number(e.target.value))}
                />
              </div>
              <div className="field" style={{ marginBottom: 0 }}>
                <label htmlFor="fatg">Lipides (g)</label>
                <input
                  id="fatg"
                  type="number"
                  min={0}
                  max={1000}
                  value={manualFat}
                  onChange={(e) => setManualFat(Number(e.target.value))}
                />
              </div>
            </div>
            {/* Répartition énergétique dérivée des grammes saisis. */}
            {(() => {
              const s = macroTargetsFromGrams(Math.round(proteinTarget), manualCarb, manualFat);
              return (
                <span className="muted" style={{ fontSize: '0.82rem' }}>
                  → répartition : {s.proteinPct}% protéines · {s.carbPct}% glucides · {s.fatPct}%
                  lipides
                </span>
              );
            })()}
          </>
        )}
      </div>

      <div className="field">
        <span className="field-label">Objectif d'hydratation</span>
        <div className="toolbar" style={{ marginBottom: '0.5rem' }}>
          <Segmented
            label="Mode de l'objectif d'hydratation"
            value={waterMode}
            options={MODE_OPTIONS}
            onChange={setWaterMode}
          />
          {waterMode === 'manual' ? (
            <input
              type="number"
              min={0}
              max={10000}
              step={50}
              value={manualWater}
              onChange={(e) => setManualWater(Number(e.target.value))}
              style={{ maxWidth: 160 }}
            />
          ) : (
            <strong style={{ fontSize: '1.3rem' }}>{Math.round(waterTarget)} ml</strong>
          )}
        </div>
        {waterMode === 'auto' && (
          <RangeWithGuideline
            label={`Ratio : ${waterMlPerKg} ml/kg`}
            min={WATER_ML_PER_KG.min}
            max={WATER_ML_PER_KG.max}
            step={1}
            value={waterMlPerKg}
            onChange={setWaterMlPerKg}
            reco={{ min: WATER_ML_PER_KG_RECO.min, max: WATER_ML_PER_KG_RECO.max }}
            note={`Repère usuel : ${WATER_ML_PER_KG_RECO.min}–${WATER_ML_PER_KG_RECO.max} ml/kg/jour (${WATER_ML_PER_KG_RECO.source})`}
          />
        )}
      </div>

      <div className="field">
        <label style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={emailReminders}
            style={{ width: 'auto' }}
            onChange={(e) => setEmailReminders(e.target.checked)}
          />
          Recevoir un rappel par email si je n'ai pas pesé dans la journée
        </label>
      </div>

      {error && <p className="error">{error}</p>}
      <button type="submit" disabled={busy}>
        {busy ? '…' : submitLabel}
      </button>
    </form>
  );
}
